import React from 'react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getLanguageDirection } from '../../i18n/config';

interface PanelHeaderProps {
  icon: React.ReactNode;
  title: string;
  tooltipText?: string;
  subtitle?: React.ReactNode;
  rightAction?: React.ReactNode;
}

export default function PanelHeader({
  icon,
  title,
  tooltipText,
  subtitle,
  rightAction
}: PanelHeaderProps) {
  const { i18n } = useTranslation();
  const isRTL = getLanguageDirection(i18n.language) === 'rtl';

  return (
    <div
      dir={getLanguageDirection(i18n.language)}
      className="flex items-center justify-between px-6 py-5 border-b bg-card sticky top-0 z-10 flex-shrink-0"
      style={{ height: '91px', minHeight: '91px' }}
    >
      {/* Left side info */}
      <div className="flex items-center flex-1">
        {/* Icon badge */}
        <div className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gold/12 border border-gold/26 text-gold ${isRTL ? 'ml-3' : 'mr-3'}`}>
          {icon}
        </div>

        <div>
          <div className="flex items-center gap-2">
            <h2 className={`text-lg font-semibold text-foreground ${isRTL ? 'text-right' : 'text-left'}`}>
              {title}
            </h2>
            
            {tooltipText && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="hover:opacity-80 transition-opacity">
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-sm">{tooltipText}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {subtitle && (
            <div className="flex items-center gap-1 mt-1">
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* Right side actions */}
      {rightAction && (
        <div className="flex-shrink-0 ml-4">
          {rightAction}
        </div>
      )}
    </div>
  );
}
